import { useRef, useEffect } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import { gsap, useGSAP } from "@ggs/ui/local/gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import StyledContainer from "./StyledContainer"
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"

const ScrollableColumns = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const row1Ref = useRef<HTMLDivElement>(null)
  const row2Ref = useRef<HTMLDivElement>(null)
  const row3Ref = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const { videoUrl, columns } = filmFestivalGlobals.scrollableColumns

  useGSAP(
    () => {
      // Register ScrollTrigger
      gsap.registerPlugin(ScrollTrigger)

      // Initialize rows with correct starting position
      gsap.set([row1Ref.current, row2Ref.current, row3Ref.current], {
        y: 100,
        opacity: 0,
        visibility: "hidden",
      })

      // Create a scroll trigger for the video - pin it for longer
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom+=100% bottom", // Make video stay pinned longer
        pin: videoContainerRef.current,
        pinSpacing: false,
      })

      // Pin the content wrapper - make sure it stays visible for the entire section
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: contentRef.current,
        pinSpacing: false,
      })

      // First row appears first - with smooth animation from bottom
      gsap.to(row1Ref.current, {
        y: 0,
        opacity: 1,
        visibility: "visible",
        ease: "power3.out", // More pronounced easing for dramatic effect
        duration: 1.2, // Longer duration for smoother animation
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "10% top", // End earlier to leave more scroll space
          scrub: 0.5, // Smoothed scrubbing
        },
      })

      // Third row appears second
      gsap.to(row3Ref.current, {
        y: 0,
        opacity: 1,
        visibility: "visible",
        ease: "power3.out",
        duration: 1.2,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "25% top", // Start earlier
          end: "45% top", // End earlier
          scrub: 0.5,
        },
      })

      // Second row appears last - ensure it's fully visible before scrolling past
      gsap.to(row2Ref.current, {
        y: 0,
        opacity: 1,
        visibility: "visible",
        ease: "power3.out",
        duration: 1.2,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "50% top", // Start earlier
          end: "70% top", // End with enough room before the section ends
          scrub: 0.5,
        },
      })

      // Create a pause at the end to ensure row2 is fully visible
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "70% top",
        end: "bottom top",
        onEnter: () => {
          // Add a small delay before scrolling past
          gsap.to(
            {},
            {
              duration: 0.5,
              onComplete: () => {
                // This empty tween just adds a slight delay
              },
            }
          )
        },
      })

      return () => {
        ScrollTrigger.getAll().forEach((t) => t.kill())
      }
    },
    { scope: containerRef, dependencies: [containerRef] }
  )

  return (
    <Box ref={containerRef}>
      <SectionContainer ref={sectionRef}>
        <VideoContainer ref={videoContainerRef}>
          <VideoElement ref={videoRef} src={videoUrl} autoPlay muted loop playsInline />
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            sx={{
              background: "#464646",
              opacity: 0.4,
            }}
          />
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            sx={{
              background:
                "linear-gradient(to top, #F05B2A 100%, #F3EAD2 0%), linear-gradient(to top, #FFCB7D 80%, #F3EAD2 0%)",
              opacity: 0.2,
            }}
          />
        </VideoContainer>

        <ContentWrapper ref={contentRef}>
          <StyledContainer maxWidth={1400} variant="secondary">
            <RowsContainer>
              <Row ref={row1Ref} alignment="left">
                <ContentBox>
                  {/* <StyledTypography variant="heading.xl">{columns.column1.text1}</StyledTypography> */}
                  <StyledTypography
                    variant="heading.xl"
                    sx={{ fontStyle: "italic", fontWeight: "400" }}
                  >
                    {columns.column1.keyword}
                  </StyledTypography>
                  <StyledTypography variant="heading.xl" lineHeight="70%!important">
                    {columns.column1.text2}
                  </StyledTypography>
                </ContentBox>
              </Row>

              <Row ref={row2Ref} alignment="center">
                <ContentBox>
                  <StyledTypography
                    variant="body.md"
                    sx={(theme) => ({
                      fontSize: "24px",
                      fontWeight: "600",
                      textAlign: "center",
                      lineHeight: "110%",
                      [theme.breakpoints.down("md")]: {
                        fontSize: "20px",
                      },
                    })}
                  >
                    {columns.column2.text}
                  </StyledTypography>
                </ContentBox>
              </Row>

              <Row ref={row3Ref} alignment="right">
                <ContentBox>
                  <StyledTypography
                    variant="heading.md"
                    sx={{ textAlign: { xs: "center", md: "right" } }}
                  >
                    {columns.column3.text}
                    <span style={{ fontStyle: "italic", fontWeight: "400", paddingLeft: "15px" }}>
                      {columns.column3.keyword}
                    </span>
                  </StyledTypography>
                </ContentBox>
              </Row>
            </RowsContainer>
          </StyledContainer>
        </ContentWrapper>
      </SectionContainer>
    </Box>
  )
}

export default ScrollableColumns

const VideoContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  overflow: "hidden",
  zIndex: 1,
}))

const VideoElement = styled("video")(({ theme }) => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
}))

const SectionContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "290vh", // Increased slightly from 200vh to give more scrolling room
  width: "100%",
}))

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  height: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden", // Added to ensure clean animations
}))

const RowsContainer = styled(Box)(({ theme }) => ({
  height: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}))

const Row = styled(Box)<{ alignment?: "left" | "center" | "right" }>(
  ({ theme, alignment = "left" }) => ({
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: alignment === "left" ? "flex-start" : alignment === "right" ? "flex-end" : "center",
    opacity: 0, // Start invisible
    transform: "translateY(100px)", // Start below its final position
    visibility: "hidden", // Hide until animation starts
    [theme.breakpoints.down("md")]: {
      alignItems: "center",
    },
  })
)

const ContentBox = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: "400px",
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: "88px",
  fontWeight: "700",
  color: theme.palette["tertiary.800"].main,
  lineHeight: "90%",
  [theme.breakpoints.down("md")]: {
    fontSize: "54px",
    textAlign: "center",
  },
}))
